import { createContext, useCallback, useEffect, useState, type ReactNode } from 'react';
import { authApi, type AuthUser } from '@/lib/auth-api';
import { notifySessionExpired } from '@/lib/auth-refresh';
import { setTelemetryUser, clearTelemetryUser } from '@/lib/telemetry';

/**
 * The fias_session cookie has a 3h Cognito-bound TTL. Refresh well before
 * that so an actively-working user never sees a 401 mid-action. 2.5h leaves
 * a 30min margin for clock skew and request latency. We can't read the
 * cookie's actual issue time (httpOnly), so this is a cadence rather than a
 * precise deadline.
 */
const SESSION_REFRESH_INTERVAL_MS = 150 * 60 * 1000;

/**
 * When the tab regains focus/visibility, re-validate the session if it's been at
 * least this long since the last refresh. Far shorter than the routine cadence so
 * a session that died while the user was away surfaces the "please sign in" prompt
 * the moment they come back, instead of letting them click around a logged-out app.
 */
const SESSION_REVALIDATE_ON_RETURN_MS = 60 * 1000;

export interface AuthContextValue {
  user: AuthUser | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  signIn: (email: string, password: string) => Promise<void>;
  signUp: (
    email: string,
    password: string,
    inviteCode: string,
    username: string,
  ) => Promise<{ requiresConfirmation: boolean }>;
  confirmSignUp: (
    email: string,
    code: string,
    inviteCode?: string,
    username?: string,
  ) => Promise<void>;
  signOut: () => Promise<void>;
  forgotPassword: (email: string) => Promise<void>;
  confirmForgotPassword: (email: string, code: string, newPassword: string) => Promise<void>;
  changePassword: (oldPassword: string, newPassword: string) => Promise<void>;
  refreshSession: () => Promise<void>;
  /** Whether the app is running in local development mode */
  isDevMode: boolean;
  /** Available dev users (empty array if not in dev mode) */
  devUsers: AuthUser[];
  /** Switch to a different dev user by index. Only works in dev mode. */
  switchDevUser: (index: number) => Promise<void>;
}

export const AuthContext = createContext<AuthContextValue | null>(null);

interface AuthProviderProps {
  children: ReactNode;
}

export function AuthProvider({ children }: AuthProviderProps) {
  const [user, setUser] = useState<AuthUser | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  // Check for existing session on mount
  useEffect(() => {
    const initializeAuth = async () => {
      try {
        const currentUser = await authApi.getCurrentUser();
        setUser(currentUser);
        if (currentUser) {
          setTelemetryUser(currentUser);
        }
      } catch {
        // No valid session
        setUser(null);
        clearTelemetryUser();
      } finally {
        setIsLoading(false);
      }
    };

    initializeAuth();
  }, []);

  const signIn = useCallback(async (email: string, password: string) => {
    const result = await authApi.signIn(email, password);
    setUser(result.user);
    setTelemetryUser(result.user);
  }, []);

  const signUp = useCallback(
    async (email: string, password: string, inviteCode: string, username: string) => {
      return authApi.signUp(email, password, inviteCode, username);
    },
    [],
  );

  const confirmSignUp = useCallback(
    async (email: string, code: string, inviteCode?: string, username?: string) => {
      await authApi.confirmSignUp(email, code, inviteCode, username);
    },
    [],
  );

  const signOut = useCallback(async () => {
    await authApi.signOut();
    setUser(null);
    clearTelemetryUser();
  }, []);

  const forgotPassword = useCallback(async (email: string) => {
    await authApi.forgotPassword(email);
  }, []);

  const confirmForgotPassword = useCallback(
    async (email: string, code: string, newPassword: string) => {
      await authApi.confirmForgotPassword(email, code, newPassword);
    },
    [],
  );

  const changePassword = useCallback(async (oldPassword: string, newPassword: string) => {
    await authApi.changePassword(oldPassword, newPassword);
    // changePassword now revokes the session in Cognito and clears local
    // storage. Reflect that in context so callers re-mount as logged-out
    // and the user is forced to re-authenticate with the new password.
    setUser(null);
  }, []);

  const refreshSession = useCallback(async () => {
    // Actually rotate the session cookie (POST /auth/refresh) — the
    // previous implementation only re-fetched /auth/me, which kept
    // the client's view of the user fresh but did nothing to extend
    // the underlying cookie's expiry.
    const ok = await authApi.refreshSession();
    if (!ok) {
      setUser(null);
      clearTelemetryUser();
      notifySessionExpired();
    }
  }, []);

  // Mirror any session-expiry signal into auth state. The api 401 interceptor,
  // streaming hooks, and the proactive refresh below all dispatch
  // `fias:session-expired` when a session can't be recovered; without reacting to
  // it here, a 401 from the api layer pops the "session expired" modal but leaves
  // `user` set — so ProtectedRoute keeps rendering and the app looks
  // logged-in-but-broken (you can click around, but every action silently fails).
  // Clearing `user` makes the whole app consistently reflect the logged-out state.
  useEffect(() => {
    const onExpired = () => {
      setUser(null);
      clearTelemetryUser();
    };
    window.addEventListener('fias:session-expired', onExpired);
    return () => window.removeEventListener('fias:session-expired', onExpired);
  }, []);

  // Proactive refresh while signed in. setInterval gets throttled or
  // suspended in background tabs by every modern browser, so we also
  // re-validate on focus/visibility when the tab returns. The user pays
  // no perceptible cost — refresh is a single cookie-only POST.
  useEffect(() => {
    if (!user) return;

    let lastRefreshAt = Date.now();

    const refreshNow = async () => {
      lastRefreshAt = Date.now();
      const ok = await authApi.refreshSession();
      if (!ok) {
        setUser(null);
        clearTelemetryUser();
        notifySessionExpired();
      }
    };

    const intervalId = window.setInterval(() => {
      if (document.visibilityState === 'visible') {
        void refreshNow();
      }
      // If hidden, skip — the focus/visibility handler below catches us
      // up when the user returns to the tab.
    }, SESSION_REFRESH_INTERVAL_MS);

    // On returning to the tab/window, re-validate promptly so an expired
    // session surfaces the sign-in prompt immediately rather than after the
    // next routine refresh tick or a failed action. Throttled so rapid tab
    // switches don't spam refreshes.
    const revalidateOnReturn = () => {
      if (document.visibilityState !== 'visible') return;
      if (Date.now() - lastRefreshAt >= SESSION_REVALIDATE_ON_RETURN_MS) {
        void refreshNow();
      }
    };

    document.addEventListener('visibilitychange', revalidateOnReturn);
    window.addEventListener('focus', revalidateOnReturn);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', revalidateOnReturn);
      window.removeEventListener('focus', revalidateOnReturn);
    };
  }, [user]);

  const switchDevUser = useCallback(async (index: number) => {
    const newUser = await authApi.switchDevUser(index);
    if (newUser) {
      setUser(newUser);
      setTelemetryUser(newUser);
    }
  }, []);

  const value: AuthContextValue = {
    user,
    isAuthenticated: !!user,
    isLoading,
    signIn,
    signUp,
    confirmSignUp,
    signOut,
    forgotPassword,
    confirmForgotPassword,
    changePassword,
    refreshSession,
    isDevMode: authApi.isDevMode(),
    devUsers: authApi.getDevUsers(),
    switchDevUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
